import { configureStore } from '@reduxjs/toolkit';
import {
  CART_STORAGE_KEY,
  cartHydrated,
  cartReducer,
  loadCartState,
  saveCartState,
} from '@/entities/cart';
import { baseApi } from '@/shared/api/baseApi';
import type { RootStateShape } from '@/shared/lib/redux/rootStateShape';
import { authReducer } from './slices/authSlice';
import { storeReducer } from './slices/storeSlice';
import { uiReducer } from './slices/uiSlice';
import { listenerMiddleware } from './listeners';

export const store = configureStore({
  reducer: {
    [baseApi.reducerPath]: baseApi.reducer,
    auth: authReducer,
    store: storeReducer,
    ui: uiReducer,
    cart: cartReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().prepend(listenerMiddleware.middleware).concat(baseApi.middleware),
});

// Persist the current user's carts (localStorage, keyed by user id, then by store) whenever they
// change. Reference equality is enough: the slice only produces a new state object when a cart was
// actually modified. Only the store carts changed in *this* tab are merged into what is stored, so
// several tabs don't overwrite each other. While nobody is signed in (expired session) nothing is
// written, so the stored cart survives until the same user comes back.
let persistedCart = store.getState().cart;
let applyingExternalChange = false;
store.subscribe(() => {
  const { cart, auth } = store.getState();
  if (cart === persistedCart) return;
  const previous = persistedCart;
  persistedCart = cart;
  if (applyingExternalChange) return; // already what storage holds — don't echo it back
  if (auth.user) saveCartState(auth.user.id, cart, previous);
});

// Another tab changed the carts: adopt its version of the current user's carts.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== CART_STORAGE_KEY) return;
    const { auth } = store.getState();
    if (!auth.user) return;
    applyingExternalChange = true;
    try {
      store.dispatch(cartHydrated(loadCartState(auth.user.id)));
    } finally {
      applyingExternalChange = false;
    }
  });
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Compile-time guard: if a slice's real state ever stops matching `shared/lib/redux/rootStateShape`
// (the hand-written contract every other layer types its Redux access against), this fails to
// build instead of silently drifting out of sync.
type _AssertRootStateMatchesSharedContract = RootState extends RootStateShape ? true : never;
const _assertRootStateMatchesSharedContract: _AssertRootStateMatchesSharedContract = true;
void _assertRootStateMatchesSharedContract;
